import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, Select, DatePicker, Button } from 'antd';
import moment from 'moment';
import { assignTraining, updateAssignmentById, getAllPrograms } from '../../../service/trainingService';
import { getAllEmployeesMethod } from '../../../service/ApiServices';

const AssignTraining = ({ selectedAssignment, handleCancel, handleReload }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const programsData = await getAllPrograms();
        setPrograms(Array.isArray(programsData) ? programsData : []);
        await getAllEmployeesMethod({
          setEmployeeData: (responseData) => {
            console.log('Employee API response:', responseData);
            const employeeData = responseData?.data || [];
            if (!Array.isArray(employeeData)) {
              console.error('Employee data is not an array:', employeeData);
              setEmployees([]);
            } else {
              setEmployees(employeeData);
            }
          },
          setLoading,
          search: '',
          params: { page: 1, limit: 1000 },
        });
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load programs or employees');
        setEmployees([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    console.log('Employees state:', employees);
    if (selectedAssignment) {
      form.setFieldsValue({
        employee: selectedAssignment.employee?._id,
        trainingProgram: selectedAssignment.trainingProgram?._id,
        dueDate: selectedAssignment.dueDate ? moment(selectedAssignment.dueDate) : null,
        status: selectedAssignment.status,
      });
    } else {
      form.resetFields();
    }
  }, [selectedAssignment, form, employees]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      if (selectedAssignment) {
        await updateAssignmentById(selectedAssignment._id, {
          employee: values.employee,
          trainingProgram: values.trainingProgram,
          dueDate: values.dueDate ? values.dueDate.toISOString() : undefined,
          status: values.status,
        });
      } else {
        await assignTraining({
          employee: values.employee,
          trainingProgram: values.trainingProgram,
          dueDate: values.dueDate ? values.dueDate.toISOString() : undefined,
        });
      }
      handleReload();
      handleCancel();
    } catch (error) {
      console.error('Error saving assignment:', error);
      setError('Failed to save training assignment');
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return <div style={{ color: 'red' }}>{error}</div>;
  }

  return (
    <Form form={form} onFinish={onFinish} layout="vertical">
      <Form.Item
        name="employee"
        label="Employee"
        rules={[{ required: true, message: 'Please select an employee' }]}
      >
        <Select
          allowClear
          showSearch
          filterOption={(input, option) =>
            option?.children?.toLowerCase().includes(input.toLowerCase())
          }
        >
          {Array.isArray(employees) && employees.length > 0 ? (
            employees.map((emp) => (
              <Select.Option key={emp._id} value={emp._id}>
                {`${emp.epf_number || "N/A"} - ${emp.full_name}`}
              </Select.Option>
            ))
          ) : (
            <Select.Option disabled>No employees available</Select.Option>
          )}
        </Select>
      </Form.Item>
      <Form.Item
        name="trainingProgram"
        label="Training Program"
        rules={[{ required: true, message: 'Please select a training program' }]}
      >
        <Select
          allowClear
          showSearch
          filterOption={(input, option) =>
            option?.children?.toLowerCase().includes(input.toLowerCase())
          }
        >
          {Array.isArray(programs) && programs.length > 0 ? (
            programs.map((prog) => (
              <Select.Option key={prog._id} value={prog._id}>
                {prog.programName}
              </Select.Option>
            ))
          ) : (
            <Select.Option disabled>No programs available</Select.Option>
          )}
        </Select>
      </Form.Item>
      <Form.Item name="dueDate" label="Due Date">
        <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="status" label="Status">
        <Select allowClear>
          <Select.Option value="Assigned">Assigned</Select.Option>
          <Select.Option value="In Progress">In Progress</Select.Option>
          <Select.Option value="Completed">Completed</Select.Option>
        </Select>
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>
          {selectedAssignment ? 'Update' : 'Assign'} Training
        </Button>
        <Button onClick={handleCancel} style={{ marginLeft: 8 }}>
          Cancel
        </Button>
      </Form.Item>
    </Form>
  );
};

AssignTraining.propTypes = {
  selectedAssignment: PropTypes.shape({
    _id: PropTypes.string,
    employee: PropTypes.shape({
      _id: PropTypes.string,
    }),
    trainingProgram: PropTypes.shape({
      _id: PropTypes.string,
    }),
    dueDate: PropTypes.string,
    status: PropTypes.string,
  }),
  handleCancel: PropTypes.func.isRequired,
  handleReload: PropTypes.func.isRequired,
};

export default AssignTraining;